import AddLinkIcon from '@mui/icons-material/AddLink';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import { ReactElement, useEffect, useState } from 'react';
import { Button, ButtonProps, Labeled, Loading, Toolbar, useResourceContext, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import MultiSelectionProvider, { useMultiSelectionContext } from '../../providers/MultiSelectionProvider';
import { Search, SearchIconWrapper, StyledInputBase } from '../common/Search';

type Choice = { id: number, name: string };
type Choices = Array<Choice>;

interface ListDialogInputModalDialogListProps {
  search: string,
  choices: Choices;
}

const ListDialogInputModalDialogList = ({ choices, search }: ListDialogInputModalDialogListProps) => {
  const [files, setFiles] = useState<Choices>([]);
  const [fileShowings, setFileShowings] = useState<Choices>([]);
  const { selection, addSelection } = useMultiSelectionContext();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setFiles(choices);
    setLoading(false);
  }, [choices]);

  useEffect(() => {
    if ('' === search) {
      setFileShowings(files)
    } else {
      setFileShowings(files.filter(x => -1 !== x.name.indexOf(search)))
    }
  }, [files, search])

  const onListItemClick = (f: Choice) => {
    addSelection(f);
  }

  if (loading) {
    return <Loading />;
  }

  return (<>
    <List>
      {fileShowings.map(f =>
        <ListItemButton key={f.name}
          selected={-1 !== selection.indexOf(f)}
          onClick={() => onListItemClick(f)}
        >
          <ListItemIcon>
            {-1 !== selection.indexOf(f)
              ? <CheckBoxIcon />
              : <CheckBoxOutlineBlankIcon />
            }
          </ListItemIcon>
          <ListItemText primary={f.name} />
        </ListItemButton>
      )}
    </List>
  </>)
}

interface ListDialogInputModalDialogProps {
  choices: Choices;
  label?: string;
}

const ListDialogInputModalDialog = ({ label, ...props }: ListDialogInputModalDialogProps) => {
  const [search, setSearch] = useState('');

  return (
    <Box
      sx={{ width: '100%', height: '90vh' }}
    >
      <Toolbar>
        <DialogTitle>{label ? label : ''}</DialogTitle>
        <span style={{ flex: 1 }}></span>
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder="Search…"
            inputProps={{ 'aria-label': 'search' }}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Search>
      </Toolbar>
      <Paper sx={{ height: 'calc(90vh - 64px - 64px)', overflowY: 'scroll' }}>
        <ListDialogInputModalDialogList {...props} search={search} />
      </Paper>
    </Box>
  )
}

interface ListDialogInputModalDialogContentProps {
  handleSelectClick: (selection: Array<any>) => void,
  handleCloseClick: () => void,
  choices: Choices;
  label?: string;
}

const ListDialogInputModalInputDialogContent = ({ handleSelectClick, handleCloseClick, choices, label }: ListDialogInputModalDialogContentProps) => {
  const translate = useTranslate();
  const { selection, clearSelection } = useMultiSelectionContext();
  const onSelectClick = () => {
    handleSelectClick(selection);
  }
  const handleClearClick = () => { clearSelection(); }
  return (
    <>
      <ListDialogInputModalDialog label={label} choices={choices} />
      <DialogActions>
        <Button onClick={onSelectClick} label={translate("ra.action.confirm")} disabled={0 === selection.length} />
        <Button onClick={handleClearClick} label={translate("ra.action.clear_input_value")} disabled={0 === selection.length} />
        <Button onClick={handleCloseClick} label={translate("ra.action.close")} />
      </DialogActions>
    </>
  )
}

interface ListDialogInputModalProps {
  showDialog: boolean,
  handleSelectClick: (selection: Array<any>) => void,
  handleCloseClick: () => void,
  label?: string,
  choices: Choices;
}
export const ListDialogInputModal = ({ showDialog, handleSelectClick, handleCloseClick, choices, label }: ListDialogInputModalProps) => {
  return (
    <Dialog
      maxWidth={"xl"}
      fullWidth
      open={showDialog}
      onClose={handleCloseClick}
      aria-label={label}
    >
      <MultiSelectionProvider>
        <ListDialogInputModalInputDialogContent label={label} handleCloseClick={handleCloseClick} handleSelectClick={handleSelectClick} choices={choices} />
      </MultiSelectionProvider>
    </Dialog>
  )
}

interface ListDialogInputModalInputProps extends Omit<ButtonProps, 'children'> {
  icon: ReactElement;
  source?: string;
  buttonOnly?: boolean;
  choices: Choices;
}

const ListDialogInput = ({ ...props }: ListDialogInputModalInputProps) => {
  const { label, icon, source, buttonOnly, choices } = props;
  const translate = useTranslate();
  const [showDialog, setShowDialog] = useState(false);
  const resource = useResourceContext();
  const formContext = useFormContext();
  const { setValue } = formContext ? formContext : { setValue: (_n: string, _v: any) => { } };

  const onClick = () => {
    setShowDialog(true);
  }

  const handleSelectClick = (selection: Array<any>) => {
    if (source) {
      setValue(source, 0 === selection.length ? null : selection[0], { shouldDirty: true });
    }
  }

  const handleCloseClick = () => {
    setShowDialog(false);
  }

  return (
    <>
      {!buttonOnly
        ? <Labeled label={translate(`resources.${resource}.fields.${source}`)}>
          <Button onClick={onClick} {...props} label={translate('')}>{icon}</Button>
        </Labeled>
        : <IconButton onClick={onClick}>{icon}</IconButton>
      }
      <ListDialogInputModal showDialog={showDialog} handleSelectClick={handleSelectClick} handleCloseClick={handleCloseClick} label={label} choices={choices} />
    </>
  );
}

ListDialogInput.defaultProps = {
  title: '',
  icon: <AddLinkIcon />,
}

export default ListDialogInput;
